// One-sentence character notes for each locality in the Warren-Blackwood electorate.
// Keyed by `${SHIRE_UPPER}|${NAME_UPPER}` so we can disambiguate any name collisions
// across shires. Tone: warm, precise, local — matched to the shire pages.

export const LOCALITY_BLURBS: Record<string, string> = {
  // ===== Augusta-Margaret River =====
  'AUGUSTA MARGARET RIVER, SHIRE OF|MARGARET RIVER':
    'The main town of the shire, with council offices, hospital and most services, and centre of the wine region.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|AUGUSTA':
    'Southern coastal town on the Blackwood River mouth, gateway to Cape Leeuwin lighthouse and the meeting of two oceans.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|COWARAMUP':
    'Small dairy town turned wine-country village on Bussell Highway, famous for its cow-themed main street.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|WITCHCLIFFE':
    'Tiny crossroads village south of Margaret River, with a pub, a general store and the gateway to the cave country.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|GRACETOWN':
    'Quiet coastal hamlet on Cowaramup Bay, popular with surfers and weekenders from Perth.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|PREVELLY':
    'Beachside settlement at the mouth of the Margaret River, home of Surfers Point and the annual pro tour event.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|GNARABUP':
    'Small coastal community immediately south of Prevelly, with a swimming beach and a long-running beach cafe.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|KARRIDALE':
    'Rural crossroads on Bussell Highway between Margaret River and Augusta, a historic timber-mill site now mostly farmland.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|HAMELIN BAY':
    'Sheltered south-coast bay known for its tame stingrays, jetty ruins and quiet camping ground.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|MOLLOY ISLAND':
    'River-bound community in the Hardy Inlet, reached only by private barge — a tight-knit boating settlement.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|EAST AUGUSTA':
    'Bushland and small-holdings east of the Hardy Inlet, with river frontage and a scattering of riverside camps.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|LEEUWIN':
    'The south-western tip of the shire and of mainland Australia — Cape Leeuwin lighthouse and the Southern Ocean coast.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|SCOTT RIVER':
    'Sweeping farmland on the Scott Coastal Plain, given over to dairying, beef and intensive horticulture.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|ALEXANDRA BRIDGE':
    'A wooded crossing of the Blackwood on Brockman Highway, with a popular bush camp by the river.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|WILYABRUP':
    'Quietly famous wine sub-region between Cowaramup and Yallingup — home to many of the founding Margaret River estates.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|METRICUP':
    'Inland farming locality north of Cowaramup, with vineyards and the back roads to Yallingup.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|TREETON':
    'Small inland locality on the back road to Nannup, mostly pasture, plantation and bush blocks.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|ROSA BROOK':
    'Hinterland farming community east of Margaret River, with its own primary school and recreation ground.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|ROSA GLEN':
    'Quiet rural locality south-east of Margaret River, sitting between Rosa Brook and the lower Blackwood forest.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|FOREST GROVE':
    'Hinterland locality of small farms and wineries on the way south to Karridale.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|OSMINGTON':
    'Rolling farmland east of Margaret River, with cattle, vineyards and lifestyle blocks along Osmington Road.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|BRAMLEY':
    'Small rural locality just north-east of Margaret River, bordering Bramley National Park.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|BURNSIDE':
    'Quiet hinterland farming locality on the upper reaches of the Margaret River.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|CHAPMAN HILL':
    'Northernmost locality of the shire, where the Margaret River wine country meets the Capel farmland.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|WALSALL':
    'Small inland locality north of Margaret River, mostly small farms and bush blocks.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|YOGANUP':
    'Northern rural locality with mineral-sand mining history and pockets of remnant bush.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|YEBBLE':
    'Small inland farming locality on the northern edge of the shire.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|KALOORUP':
    'Quiet farming locality east of Margaret River, on the road through to Nannup.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|SCHROEDER':
    'Small rural locality in the central hinterland, mostly grazing and bush blocks.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|COURTENAY':
    'Inland farming locality south-east of Margaret River, between Rosa Glen and the Blackwood forests.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|DEEPDENE':
    'Quiet hinterland locality north of Karridale, with small farms and karri-jarrah bush.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|BOALLIA':
    'Tiny rural locality on the back roads east of Margaret River.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|BAUDIN':
    'Coastal-hinterland locality named after the French explorer, between Karridale and the Scott coast.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|BORANUP':
    'Karri-forest locality between Karridale and Hamelin Bay, home to the tall-tree Boranup Forest drive.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|REDGATE':
    'Coastal locality south of Witchcliffe, with Redgate Beach and a string of well-loved surf breaks.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|KUDARDUP':
    'Small farming locality just north of Augusta, with the historic Kudardup hall and primary school.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|NILLUP':
    'Rural locality east of Karridale on the way to the Blackwood, mostly pasture and forest.',
  'AUGUSTA MARGARET RIVER, SHIRE OF|WARNER GLEN':
    'Quiet locality on the lower Blackwood River, with a popular bush camping reserve.',

  // ===== Nannup =====
  'NANNUP, SHIRE OF|NANNUP':
    'The shire’s only town — a leafy timber-and-river settlement on the Blackwood, with the council offices, school and main services.',
  'NANNUP, SHIRE OF|DONNELLY RIVER':
    'Former timber-mill village in the karri forest, now a holiday hamlet of weatherboard cottages and tame kangaroos.',
  'NANNUP, SHIRE OF|EAST NANNUP':
    'Farming and lifestyle blocks immediately east of town, along the Blackwood and Brockman valleys.',
  'NANNUP, SHIRE OF|CARLOTTA':
    'Rural locality north-east of Nannup, mostly farmland and plantation pine along the Carlotta Road.',
  'NANNUP, SHIRE OF|CUNDINUP':
    'Farming locality in the upper Blackwood Valley, with rolling pasture and remnant jarrah bush.',
  'NANNUP, SHIRE OF|JALBARRAGUP':
    'Quiet farming locality on the Blackwood north of Nannup, with a well-known riverside camping spot.',
  'NANNUP, SHIRE OF|BARRABUP':
    'Locality just north of Nannup town, home to Barrabup Pool — a long-loved swimming spot on the Blackwood.',
  'NANNUP, SHIRE OF|DARRADUP':
    'Small rural locality of farms and bush blocks on the back roads west of Nannup.',
  'NANNUP, SHIRE OF|BIDDELIA':
    'Lightly settled karri-and-farm locality between Nannup and the Margaret River hinterland.',
  'NANNUP, SHIRE OF|BRAZIER':
    'Sparsely populated forest locality south of Nannup, mostly state forest and plantation.',
  'NANNUP, SHIRE OF|PEERABEELUP':
    'Remote karri-forest locality on the southern edge of the shire, with very few residents.',
  'NANNUP, SHIRE OF|LAKE JASPER':
    'Wilderness locality around Lake Jasper, the largest freshwater lake in WA’s south-west, ringed by peppermint and tea-tree.',
  'NANNUP, SHIRE OF|SOUTHAMPTON':
    'Farming and bush locality on the lower Blackwood, with a historic homestead of the same name.',
  'NANNUP, SHIRE OF|SCOTT RIVER EAST':
    'Lightly settled forest and grazing country in the far south-west of the shire, bordering the Scott National Park.',

  // ===== Manjimup =====
  'MANJIMUP, SHIRE OF|MANJIMUP':
    'The regional capital of the southern forests — council offices, hospital, schools and the main shopping centre for the shire.',
  'MANJIMUP, SHIRE OF|PEMBERTON':
    'Karri-forest town famous for its tall trees, trout hatchery and tramway, and a growing cool-climate wine scene.',
  'MANJIMUP, SHIRE OF|NORTHCLIFFE':
    'Small forest town south of Pemberton, known for its Understory sculpture walk and the surrounding old-growth karri.',
  'MANJIMUP, SHIRE OF|WALPOLE':
    'South-coast town on the Walpole-Nornalup inlets, gateway to the Valley of the Giants tingle forest.',
  'MANJIMUP, SHIRE OF|NORTH WALPOLE':
    'Forested farming locality immediately north of Walpole, along the South Western Highway.',
  'MANJIMUP, SHIRE OF|WINDY HARBOUR':
    'Tiny shack settlement on the Southern Ocean coast, the only public access to D’Entrecasteaux National Park.',
  'MANJIMUP, SHIRE OF|JARDEE':
    'Former timber-mill village on the southern edge of Manjimup town, now a quiet residential locality.',
  'MANJIMUP, SHIRE OF|DEANMILL':
    'Historic timber-mill village just west of Manjimup, still recognisable by its rows of millworkers’ cottages.',
  'MANJIMUP, SHIRE OF|PALGARUP':
    'Farming and orchard country east of Manjimup, along the Donnelly River headwaters.',
  'MANJIMUP, SHIRE OF|BALBARRUP':
    'Quiet rural locality just north-east of Manjimup, with pasture, vineyards and lifestyle blocks.',
  'MANJIMUP, SHIRE OF|DINGUP':
    'Historic farming locality south-east of Manjimup, home to the much-photographed Dingup Church (1896).',
  'MANJIMUP, SHIRE OF|PERUP':
    'Remote forest locality in the Perup ecology centre, important habitat for numbats, woylies and chuditch.',
  'MANJIMUP, SHIRE OF|UPPER WARREN':
    'Sparsely settled karri-and-jarrah country on the upper Warren River, mostly forest and small farms.',
  'MANJIMUP, SHIRE OF|YANMAH':
    'Apple-and-stone-fruit orchard country north-east of Manjimup, with rolling cleared paddocks.',
  'MANJIMUP, SHIRE OF|RINGBARK':
    'Small farming locality north of Manjimup, named for the early settlers’ method of clearing the forest.',
  'MANJIMUP, SHIRE OF|WILGARRUP':
    'Lightly settled farming and forest locality on the back roads west of Manjimup.',
  'MANJIMUP, SHIRE OF|MIDDLESEX':
    'Rural locality south-west of Manjimup, mostly orchards, pasture and karri remnants.',
  'MANJIMUP, SHIRE OF|CHANNYBEARUP':
    'Quiet farming locality near Pemberton, with vineyards and the Channybearup primary school.',
  'MANJIMUP, SHIRE OF|EASTBROOK':
    'Forest-and-farm locality east of Pemberton, home to part of the Bibbulmun Track.',
  'MANJIMUP, SHIRE OF|BEEDELUP':
    'Karri-forest locality west of Pemberton, with the popular Beedelup Falls and walk trails.',
  'MANJIMUP, SHIRE OF|YEAGARUP':
    'Wilderness locality famous for the Yeagarup mobile dune system, one of the largest land-locked dunes in the southern hemisphere.',
  'MANJIMUP, SHIRE OF|CALLCUP':
    'Remote forest-and-coast locality on the lower Warren River, mostly national park.',
  'MANJIMUP, SHIRE OF|MEERUP':
    'Coastal-hinterland karri locality between Northcliffe and Windy Harbour, almost entirely forest.',
  'MANJIMUP, SHIRE OF|CROWEA':
    'Quiet rural locality south of Pemberton, mostly cleared farmland with patches of karri.',
  'MANJIMUP, SHIRE OF|QUINNINUP':
    'Old timber-mill settlement set among karri forest on the road between Manjimup and Northcliffe.',
  'MANJIMUP, SHIRE OF|DIAMOND TREE':
    'Forest locality named for the historic Diamond Tree fire-lookout karri (closed to climbing since 2019).',
  'MANJIMUP, SHIRE OF|GLENORAN':
    'Farming locality on the Donnelly River south-west of Manjimup, with the One Tree Bridge picnic area nearby.',
  'MANJIMUP, SHIRE OF|DIXVALE':
    'A sparsely populated forest-and-farm locality on the upper Donnelly River, mostly bush and small holdings.',
  'MANJIMUP, SHIRE OF|LAKE MUIR':
    'Wetlands locality around the Lake Muir-Unicup ramsar system, important for waterbirds.',
  'MANJIMUP, SHIRE OF|MORDALUP':
    'Remote bush-and-farm locality on the eastern edge of the shire, mostly state forest.',
  'MANJIMUP, SHIRE OF|BROKE':
    'Coastal locality at Broke Inlet, almost entirely within D’Entrecasteaux National Park.',
  'MANJIMUP, SHIRE OF|SHANNON':
    'Old timber-mill town site within Shannon National Park, now a campground and forest drive.',
  'MANJIMUP, SHIRE OF|LINFARNE':
    'Small farming locality north-east of Manjimup, mostly pasture and bush blocks.',
  'MANJIMUP, SHIRE OF|SMITH BROOK':
    'Quiet farming locality north of Manjimup along the brook of the same name.',
  'MANJIMUP, SHIRE OF|COLLINS':
    'Sparsely settled karri-forest locality south-west of Manjimup, largely state forest.',
  'MANJIMUP, SHIRE OF|BOORARA BROOK':
    'Forest locality between Northcliffe and the coast, named for the Boorara fire-lookout karri.',

  // ===== Bridgetown-Greenbushes =====
  'BRIDGETOWN-GREENBUSHES, SHIRE OF|BRIDGETOWN':
    'The main town of the shire, with the council offices, heritage main street, hospital and the November Blues festival.',
  'BRIDGETOWN-GREENBUSHES, SHIRE OF|GREENBUSHES':
    'Historic tin-mining town now home to one of the world’s largest hard-rock lithium operations.',
  'BRIDGETOWN-GREENBUSHES, SHIRE OF|NORTH GREENBUSHES':
    'Small farming locality immediately north of Greenbushes, blending mine workers’ housing and rural blocks.',
  'BRIDGETOWN-GREENBUSHES, SHIRE OF|HESTER':
    'Quiet farming locality just north of Bridgetown, with orchards and grazing along the Blackwood.',
  'BRIDGETOWN-GREENBUSHES, SHIRE OF|HESTER BROOK':
    'Small rural locality north-east of Bridgetown, named for the brook that feeds the Blackwood.',
  'BRIDGETOWN-GREENBUSHES, SHIRE OF|YORNUP':
    'Forest and farming locality south of Bridgetown, with a campground on the old railway formation.',
  'BRIDGETOWN-GREENBUSHES, SHIRE OF|KANGAROO GULLY':
    'Hilly farming locality east of Bridgetown, with orchards, vineyards and lifestyle blocks.',
  'BRIDGETOWN-GREENBUSHES, SHIRE OF|MARANUP':
    'Quiet farming locality on the Blackwood west of Bridgetown, with river-flat pasture and orchards.',
  'BRIDGETOWN-GREENBUSHES, SHIRE OF|WINNEJUP':
    'Rural locality east of Bridgetown along the Blackwood, mostly mixed farming and bush blocks.',
  'BRIDGETOWN-GREENBUSHES, SHIRE OF|CATTERICK':
    'Small farming locality north-west of Bridgetown, named after the English garrison town.',
  'BRIDGETOWN-GREENBUSHES, SHIRE OF|GLENLYNN':
    'Quiet rural locality north of Bridgetown, mostly pasture and small farms.',
  'BRIDGETOWN-GREENBUSHES, SHIRE OF|SUNNYSIDE':
    'Lightly settled hill-country locality north-east of Bridgetown, with sweeping views over the Blackwood valley.',
  'BRIDGETOWN-GREENBUSHES, SHIRE OF|KINGSTON':
    'Forest locality south-east of Greenbushes, almost entirely state forest and plantation.',
  'BRIDGETOWN-GREENBUSHES, SHIRE OF|WANDILLUP':
    'Rural locality north of Greenbushes, mostly small farms and bush blocks.',
  'BRIDGETOWN-GREENBUSHES, SHIRE OF|WILGA WEST':
    'Sparsely settled farming locality on the eastern edge of the shire, bordering Boyup Brook.',

  // ===== Boyup Brook =====
  'BOYUP BROOK, SHIRE OF|BOYUP BROOK':
    'The main town of the shire, with council offices, hospital, and country-music heritage that pulls thousands in each February.',
  'BOYUP BROOK, SHIRE OF|MAYANUP':
    'Farming locality south-east of Boyup Brook, with a small hall, recreation ground and a growing cluster of vineyards.',
  'BOYUP BROOK, SHIRE OF|DINNINUP':
    'Quiet farming locality east of Boyup Brook, home of the long-running Dinninup Show each Melbourne Cup Day.',
  'BOYUP BROOK, SHIRE OF|TONEBRIDGE':
    'Cool-climate wine and farming locality on the Tone River, increasingly known for its small-batch reds.',
  'BOYUP BROOK, SHIRE OF|KULIKUP':
    'Lightly settled farming locality south-east of Boyup Brook, with mixed cropping and grazing.',
  'BOYUP BROOK, SHIRE OF|MCALINDEN':
    'Quiet farming locality north of Boyup Brook, with a one-room school history and rolling cleared country.',
  'BOYUP BROOK, SHIRE OF|BENJINUP':
    'Rural locality west of Boyup Brook, with mixed farming and bush blocks along the upper Blackwood tributaries.',
  'BOYUP BROOK, SHIRE OF|CHOWERUP':
    'Lightly settled farming locality south of Boyup Brook, with the Chowerup Conservation Park nearby.',
  'BOYUP BROOK, SHIRE OF|WILGA':
    'Quiet rural locality on the western boundary, with forest, plantation and a scattering of farms.',
  'BOYUP BROOK, SHIRE OF|SCOTTS BROOK':
    'Quiet farming locality south of Boyup Brook, along the brook of the same name.',

  // ===== Denmark =====
  'DENMARK, SHIRE OF|DENMARK':
    'The main town of the shire, on the Denmark River and Wilson Inlet, with council offices, hospital and schools.',
  'DENMARK, SHIRE OF|OCEAN BEACH':
    'Coastal hamlet at the mouth of Wilson Inlet, popular with holiday-makers, retirees and the local surf club.',
  'DENMARK, SHIRE OF|PEACEFUL BAY':
    'Small shack-and-cottage settlement on a sheltered south-coast bay, beloved by long-time WA holiday families.',
  'DENMARK, SHIRE OF|NORNALUP':
    'Tiny inlet settlement at the edge of the Walpole-Nornalup National Park, a base for the Valley of the Giants.',
  'DENMARK, SHIRE OF|WILLIAM BAY':
    'Coastal locality named for the national park that holds Greens Pool and Elephant Rocks, among WA’s most photographed beaches.',
  'DENMARK, SHIRE OF|SCOTSDALE':
    'Rolling hill country immediately north of Denmark, threaded by the Scotsdale tourist drive of wineries and farm gates.',
  'DENMARK, SHIRE OF|SHADFORTH':
    'Quiet farming and lifestyle locality north-east of Denmark, with vineyards and small olive groves.',
  'DENMARK, SHIRE OF|HAY':
    'Farming locality east of Denmark on the road to Albany, named for the Hay River.',
  'DENMARK, SHIRE OF|HAZELVALE':
    'Rural locality north-west of Denmark, with cleared farmland and stands of remnant karri.',
  'DENMARK, SHIRE OF|KENTDALE':
    'Farming and lifestyle locality west of Denmark, along the Kent River.',
  'DENMARK, SHIRE OF|TINGLEDALE':
    'Tall-timber locality on the western edge of the shire, named for the rare red tingle trees that grow there.',
  'DENMARK, SHIRE OF|BOW BRIDGE':
    'Tiny roadhouse settlement on the South Coast Highway between Denmark and Walpole.',
  'DENMARK, SHIRE OF|MOUNT LINDESAY':
    'Sparsely settled bush locality named for the granite peak in Mt Lindesay National Park.',
  'DENMARK, SHIRE OF|MOUNT ROMANCE':
    'Quiet rural locality north of Denmark, mostly grazing and small holdings.',
  'DENMARK, SHIRE OF|KORDABUP':
    'Lightly settled farming locality west of Denmark, along the Kordabup River.',
  'DENMARK, SHIRE OF|PARRYVILLE':
    'Quiet rural locality west of Denmark, along the South Coast Highway.',
  'DENMARK, SHIRE OF|TRENT':
    'Small farming locality between Denmark and the south coast, mostly pasture and bush blocks.',
};

export function blurbFor(shireUpper: string | null | undefined, name: string | null | undefined): string | undefined {
  if (!shireUpper || !name) return undefined;
  return LOCALITY_BLURBS[`${shireUpper}|${name.toUpperCase()}`];
}
